import React, { useState, useEffect } from 'react';
import { useMapStore } from '../../store/useMapStore';
import { useAuthStore } from '../../store/useAuthStore';
import CommentDialog from './CommentDialog';

const MapDataDisplay: React.FC = () => {
  const { mapData, removeSavedMarker, clearAllMarkers, setSelectedLocation, setActiveMarker, updateComment } = useMapStore();
  const { user } = useAuthStore();
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editComment, setEditComment] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const formatCoordinate = (coord: number) => {
    return coord.toFixed(6);
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCommentClick = () => {
    console.log('Save with Comment clicked');
    console.log('Active marker:', mapData.activeMarker);
    console.log('Selected location:', mapData.selectedLocation);
    
    // When clicking "Save with Comment" for active marker, set it as selected location
    if (mapData.activeMarker) {
      setSelectedLocation(mapData.activeMarker);
      console.log('Set active marker as selected location');
    }
    setIsCommentDialogOpen(true);
    console.log('Dialog opened');
  };

  const handleDeleteActiveMarker = () => {
    console.log('Deleting active marker');
    if (window.confirm('Are you sure you want to delete this marker? This action cannot be undone.')) {
      setActiveMarker(null);
      setSelectedLocation(null);
      showToast('Marker deleted successfully');
    }
  };

  const handleCloseCommentDialog = () => {
    setIsCommentDialogOpen(false);
  };

  const handleMarkerComment = (marker: any) => {
    setSelectedLocation(marker);
    setIsCommentDialogOpen(true);
  };

  const handleClearAllMarkers = () => {
    if (window.confirm('Are you sure you want to clear all markers? This action cannot be undone.')) {
      clearAllMarkers();
      showToast('All markers cleared successfully');
    }
  };

  const handleRemoveSavedMarker = (id: string) => {
    removeSavedMarker(id);
    showToast('Saved marker removed successfully');
  };

  const handleMarkerSaved = () => {
    showToast('Marker saved successfully!');
  };

  const handleEditComment = (commentId: string, currentComment: string) => {
    setEditingCommentId(commentId);
    setEditComment(currentComment);
  };

  const handleSaveEdit = (commentId: string) => {
    if (editComment.trim()) {
      updateComment(commentId, editComment.trim());
      setEditingCommentId(null);
      setEditComment('');
      showToast('Comment updated successfully');
    } else {
      showToast('Comment cannot be empty', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditComment('');
  };

  const canEditComment = (commentEmail: string) => {
    return user?.email === commentEmail;
  };

  // Determine which location to pass to CommentDialog
  const getLocationForDialog = () => {
    // Priority: active marker first, then selected location
    return mapData.activeMarker || mapData.selectedLocation;
  };

  // Add keyboard shortcut for deleting active marker
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' && mapData.activeMarker) {
        event.preventDefault();
        handleDeleteActiveMarker();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mapData.activeMarker]);

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Map Data</h3>
          <button
            onClick={handleClearAllMarkers}
            className="px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors w-full sm:w-auto"
          >
            Clear All Markers
          </button>
        </div>

        <div className="space-y-4">
          {/* Active Marker */}
          {mapData.activeMarker && (
            <div className="bg-blue-50 p-3 rounded border border-blue-200">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium text-blue-700">Active Marker</h4>
                <div className="flex gap-2">
                  <button
                    onClick={handleCommentClick}
                    className="p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors"
                    title="Save with Comment"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                  <button
                    onClick={handleDeleteActiveMarker}
                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                    title="Delete this marker"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="text-sm text-blue-600">
                <div>Latitude: {formatCoordinate(mapData.activeMarker.lat)}</div>
                <div>Longitude: {formatCoordinate(mapData.activeMarker.lng)}</div>
                {mapData.activeMarker.address && (
                  <div>Address: {mapData.activeMarker.address}</div>
                )}
              </div>
            </div>
          )}

          {/* Saved Comments */}
          <div className="bg-gray-50 p-3 rounded">
            <h4 className="font-medium text-gray-700 mb-2">
              Saved Comments ({mapData.savedMarkers.length})
            </h4>
            {mapData.savedMarkers.length === 0 ? (
              <div className="text-sm text-gray-500">No saved comments yet</div>
            ) : (
              <div className="space-y-2">
                {mapData.savedMarkers.map((marker, index) => (
                  <div
                    key={marker.id}
                    className="bg-white p-3 rounded border border-green-200"
                  >
                    {/* Comment Header */}
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-green-700">Comment {index + 1}</div>
                      <div className="flex gap-1">
                        {canEditComment(marker.userEmail) && (
                          <button
                            onClick={() => handleEditComment(marker.commentId, marker.comment)}
                            className="p-1 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit comment"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveSavedMarker(marker.id)}
                          className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove comment"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Comment Content */}
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="text-xs text-gray-500">
                        {formatCoordinate(marker.lat)}, {formatCoordinate(marker.lng)}
                      </div>
                      {marker.address && (
                        <div className="text-xs text-gray-600 font-medium">
                          {marker.address}
                        </div>
                      )}
                      <div className="text-xs text-gray-700 font-medium">
                        Type: {marker.locationType}
                      </div>
                      {marker.unitNumber && (
                        <div className="text-xs text-gray-700 font-medium">
                          Unit: {marker.unitNumber}
                        </div>
                      )}
                      
                      {/* Editable Comment */}
                      {editingCommentId === marker.commentId ? (
                        <div className="space-y-2">
                          <textarea
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                            className="w-full p-2 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            rows={3}
                            placeholder="Edit your comment..."
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEdit(marker.commentId)}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-green-600 font-medium">
                          "{marker.comment}"
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        By: {marker.userEmail}
                      </div>
                      <div className="text-xs text-gray-400">
                        ID: {marker.commentId}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(marker.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comment Dialog */}
      <CommentDialog
        isOpen={isCommentDialogOpen}
        onClose={handleCloseCommentDialog}
        selectedLocation={getLocationForDialog()}
        onMarkerSaved={handleMarkerSaved}
      />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 left-4 sm:left-auto z-50 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ${
          toast.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          <div className="text-sm font-medium">{toast.message}</div>
        </div>
      )}
    </>
  );
};

export default MapDataDisplay; 